import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface SchoolContextType {
  schoolId: string | null;
  schoolName: string | null;
  isLoading: boolean;
}

const SchoolContext = createContext<SchoolContextType>({
  schoolId: null,
  schoolName: null,
  isLoading: true,
});

interface SchoolProviderProps {
  children: ReactNode;
}

export const SchoolProvider: React.FC<SchoolProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Get school info from user context
  const schoolId = user?.school_id || null;
  const schoolName = user?.school?.name || user?.school_info?.name || null;
  
  return (
    <SchoolContext.Provider 
      value={{ 
        schoolId, 
        schoolName,
        isLoading: false 
      }}
    >
      {children}
    </SchoolContext.Provider>
  );
};

export const useSchool = () => {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error('useSchool must be used within a SchoolProvider');
  }
  return context;
};

// Helper hook to ensure school ID exists
export const useRequireSchoolId = (): string => {
  const { schoolId } = useSchool();
  
  if (!schoolId) {
    throw new Error('School ID is required but not available. User may not be associated with a school.');
  }
  
  return schoolId;
};